import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Pagination } from '../../components/ui/Pagination'
import { useClientPagination } from '../../hooks/useClientPagination'
import { ChannelSelect } from '../../components/ChannelSelect'

interface Product {
  id: number
  sku: string
  name: string
  brand: string
  ean: string | null
  active: boolean
}

interface ProductListing {
  id: number
  productId: number
  channel: string
  channelSku: string | null
  listingUrl: string | null
  active: boolean
  product: { id: number; sku: string; name: string; brand: string; ean: string | null }
}

export function ListariCanalePage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.listings:write')

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ProductListing | null>(null)
  const [channelFilter, setChannelFilter] = useState('')

  const listingsQuery = useQuery<{ listings: ProductListing[] }>({
    queryKey: ['product-listings'],
    queryFn: async () =>
      (await api.get<{ listings: ProductListing[] }>('/marketing/product-listings')).data,
  })

  const productsQuery = useQuery<{ products: Product[] }>({
    queryKey: ['products'],
    queryFn: async () => (await api.get<{ products: Product[] }>('/products')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Partial<ProductListing>) =>
      (await api.post('/marketing/product-listings', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-listings'] })
      toast.success('Listare adăugată')
      setCreateOpen(false)
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<ProductListing> }) =>
      (await api.patch(`/marketing/product-listings/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-listings'] })
    },
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/marketing/product-listings/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-listings'] })
      toast.success('Șters')
    },
  })

  const all = useMemo(() => listingsQuery.data?.listings ?? [], [listingsQuery.data])
  const channels = useMemo(() => Array.from(new Set(all.map((l) => l.channel))).sort(), [all])
  const filtered = useMemo(
    () => (channelFilter ? all.filter((l) => l.channel === channelFilter) : all),
    [all, channelFilter],
  )
  const pag = useClientPagination(filtered, 10)
  const products = useMemo(
    () => (productsQuery.data?.products ?? []).filter((p) => p.active),
    [productsQuery.data],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Listări canale</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Listare nouă</Button>}
      </div>

      <Card description="Pe ce canale e listat fiecare produs (boost4life.ro, eMAG, Dr.Max etc.) cu URL-ul listării și flag activ.">
        <div className="mb-3 flex items-center gap-2">
          <select
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value)}
            className="rounded border border-b4l-border bg-white px-3 py-1.5 text-sm"
          >
            <option value="">Toate canalele</option>
            {channels.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <span className="ml-auto text-xs text-b4l-text-muted">
            {filtered.length} / {all.length}
          </span>
        </div>

        {listingsQuery.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : filtered.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun listing.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">SKU</th>
                  <th className="py-2 pr-3 font-medium">Produs</th>
                  <th className="py-2 pr-3 font-medium">EAN</th>
                  <th className="py-2 pr-3 font-medium">Canal</th>
                  <th className="py-2 pr-3 font-medium">SKU canal</th>
                  <th className="py-2 pr-3 font-medium">URL</th>
                  <th className="py-2 pr-3 font-medium">Activ</th>
                  {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
                </tr>
              </thead>
              <tbody>
                {pag.paginated.map((l) => (
                  <tr key={l.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3 font-mono text-xs">{l.product.sku}</td>
                    <td className="py-2 pr-3 max-w-[220px] truncate" title={l.product.name}>
                      {l.product.name}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[11px]">{l.product.ean ?? '—'}</td>
                    <td className="py-2 pr-3">
                      <span className="rounded bg-b4l-bg px-2 py-0.5 text-[10px]">{l.channel}</span>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{l.channelSku ?? '—'}</td>
                    <td className="py-2 pr-3">
                      {l.listingUrl ? (
                        <a
                          href={l.listingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-b4l-blue-dark underline hover:no-underline text-xs"
                        >
                          Deschide ↗
                        </a>
                      ) : (
                        <span className="text-xs text-b4l-text-dim">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <button
                        onClick={() =>
                          canWrite &&
                          updateMutation.mutate({ id: l.id, data: { active: !l.active } })
                        }
                        disabled={!canWrite}
                        className={
                          l.active
                            ? 'rounded bg-b4l-green-light px-2 py-0.5 text-[10px] text-b4l-green-dark'
                            : 'rounded bg-b4l-bg px-2 py-0.5 text-[10px] text-b4l-text-muted'
                        }
                      >
                        {l.active ? 'activ' : 'inactiv'}
                      </button>
                    </td>
                    {canWrite && (
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          <Button
                            variant="secondary"
                            onClick={() => setEditTarget(l)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Editează
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Sterge listing ${l.product.sku} pe ${l.channel}?`))
                                deleteMutation.mutate(l.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="listări"
          />
        </div>
      </Card>

      {createOpen && (
        <ListingFormModal
          title="Listing nou"
          submitLabel="Adaugă"
          products={products}
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          submitting={createMutation.isPending}
        />
      )}

      {editTarget && (
        <ListingFormModal
          title={`Editează ${editTarget.product.sku} pe ${editTarget.channel}`}
          submitLabel="Salvează"
          products={products}
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(data) =>
            updateMutation.mutate(
              { id: editTarget.id, data },
              { onSuccess: () => setEditTarget(null) },
            )
          }
          submitting={updateMutation.isPending}
        />
      )}
    </div>
  )
}

interface FormProps {
  title: string
  submitLabel: string
  products: Product[]
  initial?: ProductListing
  onClose: () => void
  onSubmit: (data: Partial<ProductListing>) => void
  submitting: boolean
}

function ListingFormModal({
  title,
  submitLabel,
  products,
  initial,
  onClose,
  onSubmit,
  submitting,
}: FormProps) {
  const [productId, setProductId] = useState<string>(
    initial?.productId ? String(initial.productId) : '',
  )
  const [channel, setChannel] = useState(initial?.channel ?? 'boost4life.ro')
  const [channelSku, setChannelSku] = useState(initial?.channelSku ?? '')
  const [listingUrl, setListingUrl] = useState(initial?.listingUrl ?? '')
  const [active, setActive] = useState(initial?.active ?? true)

  const submit = () => {
    if (!productId || !channel.trim()) {
      toast.error('Completează produs și canal')
      return
    }
    onSubmit({
      productId: Number(productId),
      channel: channel.trim(),
      channelSku: channelSku.trim() || null,
      listingUrl: listingUrl.trim() || null,
      active,
    } as Partial<ProductListing>)
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Produs</label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            disabled={!!initial}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm disabled:bg-b4l-bg"
          >
            <option value="">— alege produs —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.sku} — {p.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Canal</label>
          <ChannelSelect
            value={channel}
            onChange={setChannel}
            disabled={!!initial}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm disabled:bg-b4l-bg"
          />
        </div>
        <Field
          label="SKU canal (dacă diferă de SKU intern)"
          value={channelSku ?? ''}
          onChange={(e) => setChannelSku(e.target.value)}
        />
        <Field
          label="URL listing"
          value={listingUrl ?? ''}
          onChange={(e) => setListingUrl(e.target.value)}
          hint="ex: https://boost4life.ro/produse/ashwagandha"
        />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Listing activ pe canal
        </label>
      </div>
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
